// src/components/AuthHandler.tsx
import { useEffect, useRef } from 'react';
import { useNavigate, useLocation } from 'react-router-dom';
import { useAuth } from '@/contexts/AuthContext';
import { useTelegram } from '@/contexts/TelegramContext';
import { ONBOARDING_ROLE_KEY } from '@/lib/constants';

export function AuthHandler() {
  const { isAuthenticated, authStatus, isAuthenticationAttempted, authenticateWithTelegram } = useAuth();
  const { webApp, isReady: telegramReady } = useTelegram();
  const navigate = useNavigate();
  const location = useLocation();
  const authAttemptedRef = useRef(false);

  useEffect(() => {
    console.log('[AuthHandler] 🔄 Effect triggered', {
      isAuthenticated,
      authAttemptedRef: authAttemptedRef.current,
      isAuthenticationAttempted,
      hasWebApp: !!webApp,
      telegramReady,
      initDataLength: webApp?.initData?.length || 0,
    });

    // 🚨 CRITICAL FIX: Prevent infinite auth loop
    // If already authenticated OR auth was already attempted (even if failed) - DO NOT retry
    if (isAuthenticated) {
      console.log('[AuthHandler] ✅ Already authenticated, skipping auth flow');
      return;
    }

    if (authAttemptedRef.current || isAuthenticationAttempted) {
      console.log('[AuthHandler] ⏭️ Auth already attempted, preventing infinite loop', {
        authAttemptedRef: authAttemptedRef.current,
        isAuthenticationAttempted,
      });
      return;
    }

    // Ждем готовности Telegram SDK
    if (!webApp || !telegramReady) {
      console.log('[AuthHandler] ⏸️ Waiting for Telegram SDK...', { hasWebApp: !!webApp, telegramReady });
      return;
    }

    const authFlow = async () => {
      console.log('[AuthHandler] 🚀 Starting auth flow...', {
        authStatus,
        pathname: location.pathname,
        timestamp: new Date().toISOString()
      });
      
      authAttemptedRef.current = true;
      
      const savedRole = sessionStorage.getItem(ONBOARDING_ROLE_KEY);
      
      if (savedRole) {
        console.log('[AuthHandler] 🎯 Found saved role, authenticating...', { role: savedRole });
        await authenticateWithTelegram(webApp.initData);
      } else {
        // 🔥 CRITICAL FIX: Don't query database before authentication!
        // The auth endpoint will tell us if user exists via is_new_user flag
        // Querying DB here causes 401 errors since we don't have a token yet
        console.log('[AuthHandler] 📝 No saved role, will authenticate and let backend determine if user is new');
        
        // Try to authenticate - the backend will return is_new_user flag
        // If user exists, they'll be logged in automatically
        // If new user, authenticateWithTelegram will handle redirect to role selection
        console.log('[AuthHandler] 🚀 Attempting authentication (backend will check user existence)...');
        await authenticateWithTelegram(webApp.initData);
      }
    };

    authFlow().catch((error) => {
      console.error('[AuthHandler] ❌ Auth flow error:', error);
      // 🚨 CRITICAL FIX: DO NOT reset authAttemptedRef on error to prevent infinite retry loop!
      // authAttemptedRef.current = false; // ← REMOVED: This caused infinite loop!
      console.log('[AuthHandler] Auth attempt failed but will NOT retry automatically to prevent infinite loop');
    });

  // ⚠️ CRITICAL: Minimal dependencies to prevent multiple re-renders
  // Only trigger when Telegram becomes ready or authentication state fundamentally changes
  // authStatus, authenticateWithTelegram, location.pathname, navigate, telegramUser.id are intentionally omitted
  // to prevent infinite re-render loops. The effect is designed to run only when the core auth state changes.
  // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [webApp, telegramReady, isAuthenticated, isAuthenticationAttempted]);

  return null;
}